import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { PageHeader } from './PageHeader';
import { AssistantPanel } from '../AssistantPanel';

interface AppLayoutProps {
  children: React.ReactNode;
}

// Pages that have their own custom header and should skip the default PageHeader
const pagesWithCustomHeader = ['/review-notes'];

const COLLAPSE_KEY = 'vera-sidebar-collapsed';

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const hasCustomHeader = pagesWithCustomHeader.includes(location.pathname);

  // Desktop collapse state (persisted). Defaults to expanded.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Mobile/tablet drawer state — starts closed (collapsed) on small screens.
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      /* ignore storage failures */
    }
  }, [collapsed]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const layoutClasses = [
    'app-layout',
    collapsed ? 'sidebar-collapsed' : '',
    mobileOpen ? 'sidebar-mobile-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={layoutClasses}>
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onOpenMobile={() => setMobileOpen(true)}
        onCloseMobile={() => setMobileOpen(false)}
      />
      <main className="app-main">
        {!hasCustomHeader && <PageHeader />}
        <div className="app-content">
          {children}
        </div>
      </main>
      <AssistantPanel />
    </div>
  );
};
